import { Dispatch } from 'redux';
import { createSelector } from '@reduxjs/toolkit';

import {
  hasCanUsePlatesPermission,
  hasFreeSubscription,
  hasLoadCaseCreationFeatureFlag,
  hasNewLayoutModeFeatureFlag,
  hasTrussDesignPermission,
  isLoggedInUserEngineer,
} from '@paragon/login-tools';

import {
  startInsertBearing,
  startInsertGusset,
  startInsertLoadCase,
  startInsertMember,
  startInsertPlatePair,
  startInsertReferenceLine,
  startInsertSplice,
} from '../actions/insertActions';
import { startInsertComponentDesign } from '../actions/insertComponentDesignActions';
import { startInsertingBeam, startInsertingWall } from '../actions/layout/bearingEnvelopeActions';
import { startInsertingCeilingPlane } from '../actions/layout/ceilingPlaneActions';
import { startInsertingCeilingTray } from '../actions/layout/ceilingTrayActions';
import { startInsertingFeatureArea } from '../actions/layout/featureAreaActions';
import { startInsertingFeaturePatternContainer } from '../actions/layout/featurePatternContainerActions';
import { startInsertingRoofPlane } from '../actions/layout/roofPlaneActions';
import { startInsertTrussEnvelope } from '../actions/layout/trussEnvelopeActions';
import { startInsertWorkLine } from '../actions/layout/workLineActions';
import { startInsertTrussGroup } from '../actions/trussGroups';
import {
  applicationOnInsertingJackGirderConnection,
  applicationOnInsertingManualLoad,
  setDimensionMode,
} from '../slices/applicationSlice';
import { isBlockingOperationOngoing } from './blockingOperationSelectors';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { anyLayoutItemsExist, isLayoutBasedOnRoofContainers, isLayoutViewActive } from './layoutModeSelectors';

export interface InsertableObject {
  name: string;

  // `null` indicates that no keyboard shortcut should be used; `undefined` will cause one to be automatically selected
  key?: string | null;

  visible?: boolean;
  disabled?: boolean;
  dividerBefore?: boolean;

  trigger: (dispatch: Dispatch<any>) => void;
}

export function getInsertableObjectHotCharacter(obj: InsertableObject): string | null {
  switch (obj.key) {
    case null:
      return null;

    case undefined:
      return obj.name.charAt(0).toLowerCase();

    default:
      return obj.key;
  }
}

export const getInsertableObjects = createSelector(
  isLayoutViewActive,
  anyLayoutItemsExist,
  hasNewLayoutModeFeatureFlag,
  isLayoutBasedOnRoofContainers,
  getActiveComponentDesign,
  isLoggedInUserEngineer,
  hasTrussDesignPermission,
  isBlockingOperationOngoing,
  hasCanUsePlatesPermission,
  hasLoadCaseCreationFeatureFlag,
  hasFreeSubscription,

  (
    layoutViewActive,
    layoutObjectsPresent,
    hasLayoutFeatureFlag,
    inRoofContainerProject,
    activeComponentDesign,
    userIsEngineer,
    canEditTrusses,
    insertionBlockedByOngoingOperation,
    canUsePlates,
    canCreateLoadCases,
    isFreeUser,
  ) => {
    const planesEnabled = !inRoofContainerProject;

    const bearingEnvelopeItem: InsertableObject = {
      name: 'Wall',
      disabled: !hasLayoutFeatureFlag,
      trigger(dispatch) {
        dispatch(startInsertingWall());
      },
    };

    const beamItem: InsertableObject = {
      name: 'Beam',
      disabled: !hasLayoutFeatureFlag,
      trigger(dispatch) {
        dispatch(startInsertingBeam());
      },
    };

    const workLineItem: InsertableObject = {
      key: 'x',
      name: 'Work Line',
      disabled: !hasLayoutFeatureFlag,
      trigger(dispatch) {
        dispatch(startInsertWorkLine());
      },
    };

    const componentItem: InsertableObject = {
      name: 'Component',
      trigger(dispatch) {
        dispatch(startInsertComponentDesign());
      },
    };

    let result: InsertableObject[] = [];

    if (hasLayoutFeatureFlag && activeComponentDesign == null && !layoutObjectsPresent) {
      // special case hybrid menu intended for blank projects
      result = [componentItem, bearingEnvelopeItem, beamItem, workLineItem];
    } else if (layoutViewActive) {
      result = [
        {
          key: 'e',
          name: 'Truss Envelope',
          disabled: !hasLayoutFeatureFlag,
          trigger(dispatch) {
            dispatch(startInsertTrussEnvelope());
          },
        },

        {
          key: 'g',
          name: 'Truss Group',
          disabled: !hasLayoutFeatureFlag,
          trigger(dispatch) {
            dispatch(startInsertTrussGroup());
          },
        },

        bearingEnvelopeItem,

        beamItem,

        {
          name: 'Roof Plane',
          visible: planesEnabled,
          disabled: !hasLayoutFeatureFlag,
          trigger(dispatch) {
            dispatch(startInsertingRoofPlane());
          },
        },

        {
          name: 'Ceiling Plane',
          visible: planesEnabled,
          disabled: !hasLayoutFeatureFlag,
          trigger(dispatch) {
            dispatch(startInsertingCeilingPlane());
          },
        },

        {
          key: 't',
          name: 'Ceiling Tray',
          visible: planesEnabled,
          disabled: !hasLayoutFeatureFlag,
          trigger(dispatch) {
            dispatch(startInsertingCeilingTray());
          },
        },

        {
          name: 'Feature Pattern Container',
          disabled: !hasLayoutFeatureFlag,
          trigger(dispatch) {
            dispatch(startInsertingFeaturePatternContainer());
          },
        },
        {
          key: 'a',
          name: 'Chase / Step',
          disabled: !hasLayoutFeatureFlag,
          trigger(dispatch) {
            dispatch(startInsertingFeatureArea());
          },
        },

        workLineItem,

        { ...componentItem, key: null },
      ];
    } else if (canEditTrusses) {
      result = [
        // Pinned items
        {
          name: 'Member',
          trigger(dispatch) {
            dispatch(startInsertMember({ isBrace: false }));
          },
          disabled: !activeComponentDesign,
        },

        {
          name: 'Bearing',
          trigger(dispatch) {
            dispatch(startInsertBearing());
          },
          disabled: !activeComponentDesign,
        },

        {
          name: 'Splice',
          trigger(dispatch) {
            dispatch(startInsertSplice());
          },
          disabled: !activeComponentDesign,
        },

        {
          name: 'Reference Line',
          trigger(dispatch) {
            dispatch(startInsertReferenceLine());
          },
          disabled: !activeComponentDesign,
        },

        componentItem,

        // Remaining items, alphabetized
        {
          name: 'Additional Loads',
          key: null,
          dividerBefore: true,
          trigger(dispatch) {
            dispatch(applicationOnInsertingManualLoad());
          },
          disabled: !activeComponentDesign,
        },

        {
          name: 'Carried Component',
          key: null,
          trigger(dispatch) {
            dispatch(applicationOnInsertingJackGirderConnection());
          },
          disabled: !activeComponentDesign || isFreeUser,
        },

        {
          name: 'Dimension',
          trigger(dispatch) {
            dispatch(setDimensionMode(true));
          },
          disabled: !activeComponentDesign,
        },

        {
          name: 'Gusset',
          trigger(dispatch) {
            dispatch(startInsertGusset());
          },
          disabled: !userIsEngineer || !activeComponentDesign,
        },

        {
          name: 'Load Case',
          key: null,
          trigger(dispatch) {
            dispatch(startInsertLoadCase());
          },
          disabled: !activeComponentDesign || !canCreateLoadCases,
        },

        {
          name: 'Plate Pair',
          trigger(dispatch) {
            dispatch(startInsertPlatePair());
          },
          disabled: !activeComponentDesign,
          visible: canUsePlates,
        },

        {
          name: 'Scab/Brace',
          key: 'a',
          trigger(dispatch) {
            dispatch(startInsertMember({ isBrace: true }));
          },
          disabled: !userIsEngineer || !activeComponentDesign,
        },
      ];
    }

    result = result.filter((obj) => obj.visible !== false);

    // Insertion of any kind is a bad idea while a blocking operation is ongoing. A few special cases, such as creating
    //   a reference line while inserting a member, exist but do not go through the insertable objects system so a
    //   blanket ban here is still appropriate.
    if (insertionBlockedByOngoingOperation) {
      result = result.map((obj) => ({ ...obj, disabled: true }));
    }

    return result;
  },
);

export const areObjectsInsertable = createSelector(
  getInsertableObjects,
  (insertableObjects) => insertableObjects.length > 0,
);
